import clsx from "clsx";
import { useCallback, useMemo, useRef, useState } from "react";
import Image from "next/image";
import { Carousel } from "react-responsive-carousel";

import useViewport, { Viewport } from "../../hooks/useViewport";

import Icon3dTour from "../../assets/graphics/3d-tour.svg";

import Button from "../button";

import styles from "./gallery.module.scss";

const MediaTypes = {
  IMAGE: "Image",
  VIDEO: "Video",
  VIRTUAL_TOUR: "Virtual Tour"
};

const MediaImage = ({ media }) => {
  return (
    <Image
      src={`${media.image.url}?w=785&fm=webp&q=80`}
      alt={media.image.description}
      width="785"
      height="440"
      layout="responsive"
      objectFit="cover"
    />
  );
};

const MediaVideo = ({ media }) => {
  return (
    <div className={styles.VideoContainer}>
      <video autoPlay controls playsInline alt={media.image.description}>
        <source src={media.video.url} />
      </video>
    </div>
  );
};

const MediaVirtualTour = ({ media }) => {
  return (
    <div className={styles.VirtualTourContainer}>
      <div dangerouslySetInnerHTML={{ __html: media.virtualTourEmbed }}></div>
    </div>
  );
};

const DynamicMedia = ({ media }) => {
  return (
    <>
      {media.itemType === MediaTypes.IMAGE && <MediaImage media={media} />}
      {media.itemType === MediaTypes.VIDEO && <MediaVideo media={media} />}
      {media.itemType === MediaTypes.VIRTUAL_TOUR && (
        <MediaVirtualTour media={media} />
      )}
    </>
  );
};

const MainContent = ({ media }) => {
  return (
    <div className={styles.MainImage}>
      <DynamicMedia media={media} />
    </div>
  );
};

const Thumbnail = ({ image, selected, onClick }) => {
  return (
    <div className={styles.ThumbnailWrapper}>
      <Image
        className={clsx(styles.Thumbnail, selected && styles.ThumbnailSelected)}
        src={`${image.url}?w=200&fm=webp&q=80`}
        alt={image.description}
        onClick={onClick}
        layout="fill"
        objectFit="cover"
      />
    </div>
  );
};

const Thumbnails = ({ thumbnails = [], selectedIndex, onIndexChange }) => {
  const scrollRef = useRef();

  const handleThumbnailClick = useCallback(
    (event, i) => {
      // Find the width of the thumbnail.
      const count = thumbnails.length;
      const thumbnailWidth = event.target.clientWidth;

      let scrollTo = thumbnailWidth * (i - 1);

      if (i < 2) {
        scrollTo = 0;
      }

      if (i > count - 2) {
        scrollTo = thumbnailWidth * count;
      }

      scrollRef.current.scrollTo({
        left: scrollTo,
        behavior: "smooth"
      });
      onIndexChange(i);
    },
    [scrollRef, onIndexChange, thumbnails]
  );

  return (
    <div className={styles.Thumbnails} ref={scrollRef}>
      {thumbnails
        .filter((t) => t.itemType !== MediaTypes.VIRTUAL_TOUR)
        .map((t, i) => (
          <Thumbnail
            key={`thumbnail_${i}-${t.image.url}`}
            image={t.image}
            video={t.video}
            selected={selectedIndex === i}
            onClick={(event) => handleThumbnailClick(event, i)}
          />
        ))}
    </div>
  );
};

const Gallery = ({ media = [] }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(0);

  const device = useViewport();

  const [virtualTour, virtualTourIndex] = useMemo(() => {
    const index = media.findIndex(
      (m) => m.itemType === MediaTypes.VIRTUAL_TOUR
    );
    const virtualTour = media[index];
    return [virtualTour, index];
  }, [media]);

  if (device === Viewport.PHONE) {
    return (
      <div className={styles.MobileGallery}>
        <Carousel
          showArrows={false}
          showIndicators={false}
          showThumbs={false}
          showStatus={false}
          autoPlay={false}
          selectedItem={currentSlide}
          swipeScrollTolerance={20}
          preventMovementUntilSwipeScrollTolerance
          onChange={(index) => setCurrentSlide(index)}
        >
          {media.map((media, index) => (
            <DynamicMedia
              key={`carousel_${media.itemType}_${index}`}
              media={media}
            />
          ))}
        </Carousel>

        {virtualTour && (
          <div className={styles.VirtualTourButtonContainer}>
            <div className={styles.VirtualTourButtonWrapper}>
              <Button
                className={styles.VirtualTourButton}
                onClick={() => setCurrentSlide(virtualTourIndex)}
              >
                View in 3D <Icon3dTour />
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      {media[selectedIndex] && <MainContent media={media[selectedIndex]} />}

      <div className={styles.ThumbnailsWrapper}>
        <Thumbnails
          thumbnails={media}
          selectedIndex={selectedIndex}
          onIndexChange={(index) => setSelectedIndex(index)}
        />
        {virtualTour && (
          <div className={styles.VirtualTourButtonContainer}>
            <div className={styles.VirtualTourButtonWrapper}>
              <Button
                className={styles.VirtualTourButton}
                onClick={() => setSelectedIndex(virtualTourIndex)}
              >
                View in 3D <Icon3dTour />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Gallery;
